import {useController, useFormContext, useWatch} from 'react-hook-form'
import {useTranslation} from '@/translations/i18n'
import {Text} from '@/components/ui/text/Text'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {MedicalReportFieldsSchema} from '@/features/medical-reports/formSchemas'
import {z} from 'zod'
import {PlusIcon, Trash01Icon} from '@/components/ui/icon/Icon'
import {StyledMemberCard, StyledMemberCardWrapper} from './style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Button} from '@/components/ui/button/Button'
import {Divider} from '@/components/ui/divider/Divider'
import {useState} from 'react'
import {ComboboxProvider, Select, SelectProvider} from '@ariakit/react'
import {SelectComboboxInput, SelectItem, SelectList, SelectPopover} from '@/components/ui/select-atoms/SelectAtoms'

const OperatorSchema = MedicalReportFieldsSchema.pick({
    teamMembers: true,
    project: true
})
export type OperatorSchema = z.infer<typeof OperatorSchema>

export const MedicalReportOperationalSelector = ({isReadOnly = false}: {isReadOnly?: boolean}) => {
    const {t} = useTranslation()
    const form = useFormContext<OperatorSchema>()
    const projectTeam = useWatch({control: form.control, name: 'project.team'})
    const [searchValue, setSearchValue] = useState('')
    const teamMembersController = useController({control: form.control, name: 'teamMembers'})

    const handleDeleteMember = (memberId: string) => {
        const updatedMembers = teamMembersController.field.value.filter(member => member.id !== memberId)
        teamMembersController.field.onChange(updatedMembers)
    }

    const handleChangeOperator = (selectedIds: string[]) => {
        // Ottieni i membri selezionati dal team del progetto basandoti sugli ID selezionati
        const selectedMembers = projectTeam?.filter(member => selectedIds.includes(member.id)) || []
        // Sostituisci completamente la lista dei membri del team con quelli selezionati
        teamMembersController.field.onChange(selectedMembers)
    }

    // Filter project team based on search value and exclude already selected members
    const filteredProjectTeam =
        projectTeam?.filter(
            member =>
                member.fullName.toLowerCase().includes(searchValue.toLowerCase()) ||
                member.specializationName?.toLowerCase().includes(searchValue.toLowerCase())
        ) || []

    return (
        <Flexbox fullWidth direction="column" gap={3}>
            <Grid columns={2} gap={6} fullWidth>
                {teamMembersController.field.value?.map(member => (
                    <Flexbox fullWidth direction="column" gap={4} key={member.id}>
                        <StyledMemberCardWrapper>
                            <StyledMemberCard>
                                <Flexbox fullWidth direction="column">
                                    <Flexbox fullWidth justify="space-between">
                                        <Text size="textSm" weight={500} palette="neutral" shade="950">
                                            {member.fullName}
                                        </Text>
                                        <Text size="textSm" palette="neutral" shade="600">
                                            {member.isAReserve
                                                ? t('medical_records:substitute_operator')
                                                : t('medical_records:main_operator')}
                                        </Text>
                                    </Flexbox>
                                    <Text size="textSm" palette="neutral" shade="600">
                                        {member.specializationName}
                                    </Text>
                                </Flexbox>
                            </StyledMemberCard>
                            {!isReadOnly && (
                                <Button shape="square" variant="ghost" onClick={() => handleDeleteMember(member.id)}>
                                    <Trash01Icon size={20} />
                                </Button>
                            )}
                        </StyledMemberCardWrapper>
                        <Divider direction="horizontal" />
                    </Flexbox>
                ))}
            </Grid>
            {!isReadOnly && (
                <ComboboxProvider setValue={setSearchValue} resetValueOnHide>
                    <SelectProvider
                        value={teamMembersController.field.value?.map(value => value.id) || []}
                        setValue={handleChangeOperator}
                    >
                        <Select
                            render={
                                <Button variant="secondary">
                                    <PlusIcon size={20} />
                                    {t('commons:operator')}
                                </Button>
                            }
                        />
                        <SelectPopover sameWidth={false}>
                            <SelectComboboxInput placeholder={t('commons:search_operator')} />
                            {filteredProjectTeam.length > 0 ? (
                                <SelectList>
                                    {filteredProjectTeam.map(operator => (
                                        <SelectItem hasItemCheckbox key={operator.id} value={operator.id}>
                                            <Flexbox gap={1}>
                                                {operator.fullName}
                                                <Text size="textSm" palette="neutral" shade="600">
                                                    ({operator.specializationName})
                                                </Text>
                                            </Flexbox>
                                        </SelectItem>
                                    ))}
                                </SelectList>
                            ) : (
                                <SelectItem disabled>{t('medical_records:no_operators_present')}</SelectItem>
                            )}
                        </SelectPopover>
                    </SelectProvider>
                </ComboboxProvider>
            )}
        </Flexbox>
    )
}
